/**
 * The kid's money screen. Their name, their colour, three buckets with big bars, and
 * their goals as cards that fill up like a game. A reached goal celebrates. A kid can
 * add, edit and remove their OWN goals — the Worker refuses anything else.
 *
 * A kid only ever sees their own numbers here: everything comes from endpoints that
 * derive the member from the verified identity, so there is no id to tamper with.
 */
import { useState } from 'react';
import { api, ApiRequestError, money } from '../../lib/api';
import type { KidGoal, KidWallet, MemberSummary } from '../../shared/types';
import GoalForm from './GoalForm';
import {
  Avatar,
  Banner,
  BUCKETS,
  BucketBar,
  Card,
  EmptyState,
  GoalBar,
  SectionTitle,
  bucketMeta,
  btnPrimary,
  btnSecondary,
  cx,
  walletTotal,
} from './shared';

export default function KidDashboard({
  me,
  wallet,
  goals,
  reload,
}: {
  me: MemberSummary | null;
  wallet: KidWallet;
  goals: KidGoal[];
  reload: () => Promise<void>;
}) {
  const [editing, setEditing] = useState<KidGoal | null>(null);
  const [creating, setCreating] = useState(false);
  const [flash, setFlash] = useState<{ tone: 'ok' | 'error'; message: string } | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const color = me?.color ?? 'var(--color-sage)';
  const total = walletTotal(wallet);
  const reached = goals.filter((g) => g.achieved_at);

  async function removeGoal(goal: KidGoal) {
    if (!window.confirm(`Remove your “${goal.name}” goal? Your money stays safe in the bucket.`))
      return;
    setBusyId(goal.id);
    setFlash(null);
    try {
      await api.del(`/kidmoney/goals/${goal.id}`);
      await reload();
      setFlash({ tone: 'ok', message: `“${goal.name}” is off your list. Your money stayed put.` });
    } catch (err) {
      setFlash({
        tone: 'error',
        message:
          err instanceof ApiRequestError ? err.message : 'That did not save. Try again in a second.',
      });
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="space-y-6">
      {/* Hero — their name, their colour, their total. */}
      <div
        className="rounded-card border border-border p-5"
        style={{ backgroundColor: `color-mix(in srgb, ${color} 10%, var(--color-surface))` }}
      >
        <div className="flex items-center gap-4">
          <Avatar member={me} size="lg" />
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-semibold text-ink">
              Hi, {me?.name ?? 'friend'}
            </h1>
            <p className="text-sm text-ink-soft">Here’s your money.</p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-xs font-medium tracking-wide text-ink-soft uppercase">Altogether</p>
            <p className="font-display text-4xl font-semibold" style={{ color }}>
              {money(total)}
            </p>
          </div>
          {wallet.pending > 0 ? (
            <p className="rounded-full bg-honey-soft px-3 py-1 text-sm font-semibold text-honey">
              ⏳ {money(wallet.pending)} waiting on a grown-up
            </p>
          ) : null}
        </div>
      </div>

      {flash ? <Banner tone={flash.tone} message={flash.message} /> : null}

      {/* Celebration when a goal is reached */}
      {reached.length > 0 ? (
        <div className="rounded-card border-2 border-sage bg-sage-soft p-4 text-center">
          <p className="text-3xl leading-none" aria-hidden="true">
            🎉🎈🎉
          </p>
          <p className="mt-2 font-display text-lg font-semibold text-sage">
            {reached.length === 1
              ? `You reached your goal: ${reached[0].name}!`
              : `You reached ${reached.length} goals!`}
          </p>
          <p className="mt-1 text-sm text-ink-soft">
            Ask a grown-up when you’re ready to spend it.
          </p>
        </div>
      ) : null}

      {/* Buckets */}
      <div>
        <SectionTitle>Your buckets</SectionTitle>
        <div className="grid gap-3 sm:grid-cols-3">
          {BUCKETS.map((b) => (
            <Card key={b.key} className="flex flex-col gap-3">
              <div className="flex items-center gap-2">
                <span aria-hidden="true" className="text-2xl">
                  {b.emoji}
                </span>
                <div>
                  <p className="font-display text-base font-semibold text-ink">{b.label}</p>
                  <p className="text-xs text-ink-faint">{b.blurb}</p>
                </div>
              </div>
              <p className={cx('font-display text-2xl font-semibold', b.text)}>
                {money(wallet[b.key])}
              </p>
              <BucketBar meta={b} amount={wallet[b.key]} total={total} />
            </Card>
          ))}
        </div>
      </div>

      {/* Goals */}
      <div>
        <SectionTitle
          action={
            goals.length > 0 ? (
              <button type="button" onClick={() => setCreating(true)} className={btnPrimary}>
                + New goal
              </button>
            ) : undefined
          }
        >
          Your goals
        </SectionTitle>

        {goals.length === 0 ? (
          <EmptyState
            title="What are you saving for?"
            body="Pick something you want — a bike, a game, anything — and watch your Save bucket fill it up."
            action={
              <button type="button" onClick={() => setCreating(true)} className={btnPrimary}>
                Set your first goal
              </button>
            }
          />
        ) : (
          <ul className="grid gap-3 sm:grid-cols-2">
            {goals.map((goal) => (
              <li key={goal.id}>
                <GoalCard
                  goal={goal}
                  busy={busyId === goal.id}
                  onEdit={() => setEditing(goal)}
                  onRemove={() => void removeGoal(goal)}
                />
              </li>
            ))}
          </ul>
        )}
      </div>

      {creating ? (
        <GoalForm
          memberId={me?.id ?? null}
          onClose={() => setCreating(false)}
          onSaved={reload}
        />
      ) : null}
      {editing ? (
        <GoalForm
          goal={editing}
          memberId={editing.member_id}
          onClose={() => setEditing(null)}
          onSaved={reload}
        />
      ) : null}
    </div>
  );
}

function GoalCard({
  goal,
  busy,
  onEdit,
  onRemove,
}: {
  goal: KidGoal;
  busy: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  const meta = bucketMeta(goal.bucket);
  const done = !!goal.achieved_at;
  const remaining = Math.max(0, goal.target - goal.saved);

  const status = done
    ? 'Reached! 🎉'
    : goal.percent >= 75
      ? `Almost there — ${money(remaining)} to go!`
      : `${money(remaining)} to go`;

  return (
    <Card
      className={cx('flex h-full flex-col gap-3', done && 'border-2 border-sage bg-sage-soft')}
    >
      <div className="flex items-start gap-3">
        <span aria-hidden="true" className="text-3xl leading-none">
          {goal.emoji ?? '🎯'}
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-lg font-semibold text-ink">{goal.name}</h3>
          <p className="text-sm text-ink-soft">
            {money(goal.saved)} of {money(goal.target)}
          </p>
        </div>
        <span
          className={cx(
            'rounded-full px-2.5 py-0.5 text-xs font-semibold',
            meta.soft,
            meta.text
          )}
        >
          {meta.emoji} {meta.label}
        </span>
      </div>

      <GoalBar percent={goal.percent} accent={done ? 'var(--color-sage)' : meta.accent} />

      <p className={cx('text-sm font-medium', done ? 'text-sage' : 'text-ink-soft')}>{status}</p>

      <div className="mt-auto flex gap-2 pt-1">
        <button type="button" onClick={onEdit} disabled={busy} className={cx(btnSecondary, 'flex-1')}>
          Edit
        </button>
        <button
          type="button"
          onClick={onRemove}
          disabled={busy}
          className="inline-flex min-h-[44px] items-center justify-center rounded-xl px-3 text-sm font-medium text-ink-soft transition-colors hover:bg-surface-sunk hover:text-clay focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage disabled:opacity-50"
        >
          {busy ? '…' : 'Remove'}
        </button>
      </div>
    </Card>
  );
}
